// src/lib/api/index.js
import axios from 'axios';
import { useState, useCallback } from 'react';
import { toastService } from '@/lib/toast';
import useAuthStore from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL;

// Create base axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage =
      error.response?.data?.error?.message ||
      error.response?.data.message ||
      'Something went wrong';

    // Check for token expiration: 401 error when user appears to be logged in
    // This indicates their session has expired since they have user data but got unauthorized
    if (error.response?.status === 401) {
      const { user, handleTokenExpiration } = useAuthStore.getState();

      // Only trigger logout if user appears to be logged in (has user data)
      // This prevents logout on expected 401s when user is already logged out
      if (user) {
        console.log(
          'User appears logged in but received 401 - likely token expiration'
        );

        // Show user-friendly notification for session expiration
        toastService.error('Your session has expired. Please sign in again.');

        handleTokenExpiration();

        // Don't show the generic error toast for token expiration
        return Promise.reject(error);
      }
    }

    toastService.error(errorMessage);
    return Promise.reject(error);
  }
);

// Custom hook for API requests
export const useApi = () => {
  const [loading, setLoading] = useState(false);

  const request = useCallback(
    async ({ method = 'GET', url, data, successMessage, ...config }) => {
      setLoading(true);

      try {
        const requestConfig = {
          method,
          url,
          ...config,
        };

        if (data instanceof FormData) {
          requestConfig.data = data;
          requestConfig.headers = {
            ...requestConfig.headers,
            'Content-Type': 'multipart/form-data',
          };
        } else if (data !== undefined) {
          requestConfig.data = data;
        }

        const response = await axiosInstance.request(requestConfig);

        if (successMessage) {
          toastService.success(successMessage);
        }

        return response.data;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    request,
    loading,
  };
};

// Export the axios instance for direct use if needed
export const api = axiosInstance;
export { axiosInstance }; // Export the instance directly for use in non-hook contexts

// Removed standalone createBooking function as useApi hook is used directly in the component
